/* eslint-disable no-proto, no-param-reassign */
import Backbone from 'backbone';
import $ from 'jquery';
import _ from 'underscore';
import { DateFilter } from '@bingads-webui/date-filter';
import { getHotelCenterUtils } from '@bingads-webui-campaign/hotel-center-utils';
import { withoutTime } from '@bingads-webui/primitive-utilities';
import { RANGES } from '@bingads-webui/date-ranges';

export const getProjectionGridDateFilter = ({
  i18n: i18nDefault, pageContext, state, dateFilterPreferences,
}) => {
  if (!i18nDefault || !pageContext || !state || !dateFilterPreferences) {
    return {};
  }
  const { dateUtils } = getHotelCenterUtils({ pageContext });
  const { setDateFilter } = dateFilterPreferences;
  const { account2local, local2account } = dateUtils;

  const includeRanges = ['custom', 'today', 'yesterday', 'thisWeekSunday', 'thisWeekMonday', 'lastSevenDays',
    'lastWeekSunday', 'lastWeekMonday', 'lastFourteenDays', 'thisMonth', 'lastMonth', 'last30Days', 'allTime'];

  const formatAccountDate = date =>
    i18nDefault.formatDate(account2local(date), { raw: 'MM/dd/yyyy' });

  const formatUTCDateTime = (date) => {
    const strDate = i18nDefault.formatDate(account2local(date), { raw: 'yyyy-MM-ddTHH:mm:ss.sss', timeZone: 'GMT' });

    return `${strDate}Z`;
  };

  const ensureMinDate = (date, minDate) => {
    if (!_.isObject(date)) {
      date = new Date(date);
    }
    return minDate && (date < minDate) ? minDate : date;
  };

  const parseDate = dateString => i18nDefault.parseDate(dateString);

  const getDefaultRange = today => ({
    grain: 3,
    start: today,
    end: today,
    key: 'today',
  });

  const normalizeConfig = (config, gridName) => {
    const i18n = _.defaults({
      timeZone: null,
      __proto__: i18nDefault.__proto__,
    }, i18nDefault);

    // Normalize the config with default options
    return _.defaults(config, {
      i18n,
      isComparisonEnabled: true,
      enabledRangesPredicate: range => _.indexOf(includeRanges, range.key) !== -1,
      dateFormat: 'yMd',
      timeFormat: 'hm',
      get maxDate() {
        return config.maxDate || withoutTime(local2account(new Date()));
      },
      get defaultTimeRange() {
        const storedDateRangeKey = config.sharedInGrids === false ? `$daterange${gridName}` : '$daterange';
        const storedDateRange = state.get(storedDateRangeKey);

        if (_.result(storedDateRange, 'key') === RANGES.CUSTOM_TIME) {
          return {
            start: ensureMinDate(storedDateRange.start, config.minDate),
            end: ensureMinDate(storedDateRange.end, config.minDate),
          };
        }
        return _.noop();
      },
    });
  };

  const generateEndDateTime = (date, upToNowWhenToday) => {
    const now = local2account(new Date());
    const today = withoutTime(now);

    // if it is today, get the time for now
    if (date.valueOf() === today.valueOf() && upToNowWhenToday) {
      return now;
    }
    // if it is a past day, get the last time of that day
    const returnDate = new Date(date);

    returnDate.setHours(23, 59, 59, 999);
    return returnDate;
  };

  const formatDateRange = (odataTimeZone, dateRange, upToNowWhenToday) => _.result({
    UTC: () => {
      const end = dateRange.key === RANGES.CUSTOM_TIME ?
        dateRange.end :
        generateEndDateTime(dateRange.end, upToNowWhenToday);

      return {
        startDateTime: formatUTCDateTime(dateRange.start),
        endDateTime: formatUTCDateTime(end),
      };
    },
    Account: () => ({
      startDate: formatAccountDate(dateRange.start) ||
            formatAccountDate(parseDate(dateRange.RangeStartDate)),
      endDate: formatAccountDate(dateRange.end) ||
            formatAccountDate(parseDate(dateRange.RangeEndDate)),
    }),
  }, odataTimeZone);

  const addDateRangeToQueryParams = (queryParams, dateRange, { odataTimeZone = 'Account', upToNowWhenToday = true }) => {
    // need to format the date when generate from the preference odata api the first time
    if (dateRange.start && !_.isObject(dateRange.start) &&
        dateRange.end && !_.isObject(dateRange.end)) {
      dateRange.start = new Date(dateRange.start);
      dateRange.end = new Date(dateRange.end);
    }

    // Feeds use date time as date range while others use date
    const primaryRange = formatDateRange(odataTimeZone, dateRange, upToNowWhenToday);
    let query = _.defaults(primaryRange, queryParams.query);

    if (dateRange.compared) {
      query = _.defaults({
        comparisonStartDate: formatAccountDate(new Date(dateRange.compared.start)),
        comparisonEndDate: formatAccountDate(new Date(dateRange.compared.end)),
      }, query);
    } else {
      query = _.omit(query, 'comparisonStartDate', 'comparisonEndDate');
    }

    return _.defaults({
      query,
    }, queryParams);
  };

  const renderDateFilterView = (dateRange, dateFilter, dateFilterView) => {
    const today = withoutTime(local2account(new Date()));
    const container = $(dateFilter.container);
    const selection = _.extend({
      primaryRange: {
        grain: dateRange.grain,
        key: dateRange.key,
        start: ensureMinDate(dateRange.start, dateFilter.minDate),
        end: ensureMinDate(dateRange.end, dateFilter.minDate),
      },
    }, dateRange.compared ? {
      comparedRange: {
        key: dateRange.compared.key,
      },
    } : {});

    dateFilterView.setState({ today, selection });
    dateFilterView.render();
    container.append(dateFilterView.$el);
  };

  const setDateRange = ({
    name,
    dateFilter,
    dateRange,
  }) => {
    const { primaryRange, comparedRange } = dateRange;
    _.extend(primaryRange, { Rangelcid: pageContext.CurrentUser.Locale });
    const selectedDate = _.extend(primaryRange, {
      compared: comparedRange,
    });

    if (dateFilter.sharedInGrids === false) {
      setDateFilter(name, _.clone(dateRange));
      state.set(`$daterange${name}`, selectedDate);
    } else {
      setDateFilter('TravelShared', _.clone(dateRange));
      state.set('$daterange', selectedDate);
    }
  };

  const dateFilterPlugin = definePlugin =>
    definePlugin('dateFilter', ['gridView', 'dataSource', 'config'], (gridView, dataSource, { plugins, name } = {}) => {
      const dateFilter = _.result(plugins, 'dateFilter', {});
      const normalizedConfig = normalizeConfig(dateFilter, name);
      const dateFilterView = new DateFilter(normalizedConfig);
      const projectName = dateFilter.sharedInGrids === false ? `$daterange${name}` : '$daterange';
      const originalQuery = dataSource.query;

      // render date filter view in the initial function
      state.getPromise(projectName).then((initDateRange) => {
        let dateRange = initDateRange;

        // rewrite the date filter here to incase
        // preference setting saved some unenabled ranges
        if (!dateFilter.enabledRangesPredicate(dateRange)) {
          const today = withoutTime(local2account(new Date()));

          dateRange = getDefaultRange(today);
          setDateRange({
            name,
            dateFilter,
            dateRange:
              {
                primaryRange: _.omit(dateRange, 'compared'),
                comparedRange: dateRange.compared,
              },
          });
        }
        renderDateFilterView(dateRange, dateFilter, dateFilterView);
      });

      // wrap dateSource query to update data range
      dataSource.query = params => state.getPromise(projectName).then((dateRange) => {
        const queryParams = addDateRangeToQueryParams(params, dateRange, dateFilter);

        gridView.set({ queryParams });

        return originalQuery.call(dataSource, queryParams);
      });

      dateFilterView.on('date-range-selected', (dateRange) => {
        setDateRange({ name, dateFilter, dateRange });

        gridView.refresh(true);
      });
      return { events: _.extend({}, Backbone.Events) };
    });
  return {
    dateFilterPlugin,
    addDateRangeToQueryParams,
  };
};
